import { capitalizeWords } from "@rebuild/shared";
import { useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { PhotoToolbarPanelHeader } from "./PhotoToolbarPanelHeader";

export interface RoomOption {
  value: string;
  label: string;
}

interface PhotoMovePanelProps {
  photoCount: number;
  roomOptions: RoomOption[];
  /** Set when every selected photo already lives in the same room; that room can't be a destination. */
  currentRoomId?: string;
  isPending: boolean;
  onSelectRoom: (room: RoomOption) => void;
  onClose: () => void;
}

export function PhotoMovePanel({ photoCount, roomOptions, currentRoomId, isPending, onSelectRoom, onClose }: PhotoMovePanelProps) {
  const [query, setQuery] = useState("");

  const visibleRooms = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    return [...roomOptions]
      .sort((a, b) => a.label.localeCompare(b.label))
      .filter((room) => room.label.toLowerCase().includes(normalizedQuery));
  }, [roomOptions, query]);

  return (
    <div className="flex flex-col gap-1.5" id="photo-toolbar-move-panel">
      <PhotoToolbarPanelHeader title={`Move ${photoCount} photo${photoCount === 1 ? "" : "s"} to…`} onClose={onClose} />
      <Input
        autoFocus
        value={query}
        placeholder="Search rooms"
        aria-label="Search rooms"
        className="h-8 border-gray-700 bg-transparent text-white placeholder:text-gray-400"
        onChange={(event) => setQuery(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            onClose();
          }
        }}
      />
      {/* Capped at a little over four rows so the next one peeks out and signals that the list scrolls. */}
      <ul className="max-h-32 overflow-y-auto">
        {visibleRooms.map((room) => {
          const isCurrent = room.value === currentRoomId;
          return (
            <li key={room.value}>
              <button
                type="button"
                disabled={isCurrent || isPending}
                className={cn(
                  "flex w-full items-center justify-between rounded-md px-2 py-1 text-left text-sm text-gray-200 transition-colors",
                  "hover:bg-gray-800 hover:text-white focus-visible:bg-gray-800 focus-visible:outline-none disabled:pointer-events-none",
                  isPending && !isCurrent && "opacity-50",
                )}
                onClick={() => onSelectRoom(room)}
              >
                <span>{capitalizeWords(room.label)}</span>
                {isCurrent && <span className="text-xs text-gray-400">Current</span>}
              </button>
            </li>
          );
        })}
        {visibleRooms.length === 0 && <li className="px-2 py-1.5 text-sm text-gray-400">No rooms found</li>}
      </ul>
    </div>
  );
}
